import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useVdbStatus } from '@/lib/useVdb';

/** Re-invalidate VDB-backed reads when a server-side VDB build COMPLETES.
 *
 *  A data write returns immediately with the rebuild only *scheduled*, and the
 *  writing surface invalidates the cache right away — so the refetch races the
 *  still-running build and re-caches pre-load data for another stale cycle
 *  (this is the "catalog didn't register the upload" symptom). Watching the
 *  build's `building -> ready` (or `-> failed`) transition and invalidating on
 *  completion guarantees one refetch against the finished index, independent of
 *  which surface triggered the load. Cheap: it fires only on an actual
 *  transition, which happens only after a real data write. */
export function VdbRebuildWatcher() {
  const status = useVdbStatus();
  const queryClient = useQueryClient();
  const prevState = useRef<string | undefined>(undefined);

  useEffect(() => {
    const state = status.data?.state;
    const prev = prevState.current;
    prevState.current = state;
    // Only a transition OUT of an in-flight build settles new data. Startup
    // (undefined/pending -> ready) also lands here and harmlessly refetches
    // once against the freshly built index.
    if (
      prev === 'building' &&
      (state === 'ready' || state === 'failed' || state === 'deferred')
    ) {
      void queryClient.invalidateQueries();
    }
  }, [status.data?.state, queryClient]);

  return null;
}
